import React, { useCallback, useMemo, useState } from 'react';
import {
  Dialog, Box, Stack, Typography, Button, IconButton, alpha, useTheme,
  TextField, CircularProgress, Chip, Divider,
} from '@mui/material';
import {
  Close, DeleteForeverOutlined, WarningAmberOutlined, ExitToApp,
  Receipt, AssignmentOutlined, Mail as MailIcon, PaymentsOutlined,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { studentsApi } from '../../services/sessions/studentsApi';

interface Props {
  open: boolean;
  studentId: string | null;
  studentName?: string;
  onClose: () => void;
  /** Called only on successful hard delete. Parent should close the drawer
   *  and refresh whatever list spawned it. */
  onDeleted: () => void;
  /** Optional shortcut to "close current enrollment" when the delete is
   *  blocked by history — gives the user a one-click out. */
  onCloseEnrollment?: () => void;
}

interface BlockCounts {
  attendance: number;
  results: number;
  letters: number;
  finePayments: number;
}

type Phase = 'confirm' | 'blocked';

/**
 * Two-phase modal for permanent student delete:
 *   1. confirm — type the student name to confirm; submit fires the request.
 *   2. blocked — 409 from server; show the counts of related records and
 *      route the user to "close enrollment" instead.
 */
const DeleteStudentDialog: React.FC<Props> = ({
  open, studentId, studentName, onClose, onDeleted, onCloseEnrollment,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [phase, setPhase] = useState<Phase>('confirm');
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [block, setBlock] = useState<BlockCounts | null>(null);

  // Reset state when the dialog closes so reopening starts fresh.
  React.useEffect(() => {
    if (!open) {
      setPhase('confirm');
      setConfirmText('');
      setSubmitting(false);
      setBlock(null);
    }
  }, [open]);

  const expectedName = (studentName || '').trim();
  const canSubmit = useMemo(
    () => Boolean(studentId) && confirmText.trim() === expectedName && expectedName.length > 0,
    [studentId, confirmText, expectedName]
  );

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose();
  }, [submitting, onClose]);

  const handleDelete = useCallback(async () => {
    if (!studentId || !canSubmit) return;
    setSubmitting(true);
    try {
      await studentsApi.remove(studentId);
      enqueueSnackbar(`${studentName || 'Student'} deleted permanently`, { variant: 'success' });
      onDeleted();
    } catch (err: any) {
      const status = err?.status;
      const details = err?.details;
      if (status === 409 && details?.block) {
        setBlock(details.block as BlockCounts);
        setPhase('blocked');
      } else {
        enqueueSnackbar(err?.message || 'Failed to delete student', { variant: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }, [studentId, canSubmit, studentName, enqueueSnackbar, onDeleted]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 24px 60px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.06)',
          },
        },
      }}
    >
      {phase === 'confirm' ? (
        <ConfirmPhase
          studentName={studentName}
          expectedName={expectedName}
          confirmText={confirmText}
          onChange={setConfirmText}
          canSubmit={canSubmit}
          submitting={submitting}
          onCancel={handleClose}
          onDelete={handleDelete}
        />
      ) : (
        <BlockedPhase
          studentName={studentName}
          block={block}
          onClose={handleClose}
          onCloseEnrollment={onCloseEnrollment}
        />
      )}
    </Dialog>
  );
};

// ─── Confirm phase ─────────────────────────────────────────────────────────

const ConfirmPhase: React.FC<{
  studentName?: string;
  expectedName: string;
  confirmText: string;
  onChange: (v: string) => void;
  canSubmit: boolean;
  submitting: boolean;
  onCancel: () => void;
  onDelete: () => void;
}> = ({ studentName, expectedName, confirmText, onChange, canSubmit, submitting, onCancel, onDelete }) => {
  const theme = useTheme();

  return (
    <>
      {/* Hero band — error tint so the destructive nature is obvious */}
      <Box
        sx={{
          position: 'relative',
          py: 3,
          px: 3,
          textAlign: 'center',
          background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.06)} 0%, ${alpha(theme.palette.error.main, 0.12)} 100%)`,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <IconButton
          onClick={onCancel}
          size="small"
          aria-label="Close"
          disabled={submitting}
          sx={{
            position: 'absolute', top: 8, right: 8,
            color: 'text.disabled',
            '&:hover': { color: 'text.secondary', bgcolor: alpha(theme.palette.text.primary, 0.04) },
          }}
        >
          <Close sx={{ fontSize: 18 }} />
        </IconButton>

        <Box
          sx={{
            width: 56, height: 56, mx: 'auto', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'background.paper',
            border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
            color: theme.palette.error.main,
          }}
        >
          <DeleteForeverOutlined sx={{ fontSize: 30 }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mt: 1.5, lineHeight: 1.2 }}>
          Delete student permanently?
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, maxWidth: 360, mx: 'auto' }}>
          This removes <strong>{studentName || 'this student'}</strong> and their enrollments. Allowed only when the student has no attendance, results, letters, or fine payments.
        </Typography>
      </Box>

      <Box sx={{ px: 3, py: 2.5 }}>
        <Typography variant="caption" sx={{ color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600, fontSize: '0.65rem', display: 'block', mb: 1 }}>
          Type the student's name to confirm
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={confirmText}
          onChange={(e) => onChange(e.target.value)}
          placeholder={expectedName}
          autoFocus
          disabled={submitting}
          autoComplete="off"
          spellCheck={false}
        />
      </Box>

      <Box
        sx={{
          px: 3, py: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.background.default, 0.4),
        }}
      >
        <Button onClick={onCancel} color="inherit" disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={onDelete}
          variant="contained"
          color="error"
          disableElevation
          disabled={!canSubmit || submitting}
          startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <DeleteForeverOutlined sx={{ fontSize: 18 }} />}
          sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, px: 2 }}
        >
          {submitting ? 'Deleting…' : 'Delete permanently'}
        </Button>
      </Box>
    </>
  );
};

// ─── Blocked phase ─────────────────────────────────────────────────────────

const BlockedPhase: React.FC<{
  studentName?: string;
  block: BlockCounts | null;
  onClose: () => void;
  onCloseEnrollment?: () => void;
}> = ({ studentName, block, onClose, onCloseEnrollment }) => {
  const theme = useTheme();

  const rows: Array<{ label: string; count: number; icon: React.ReactNode; tone: string }> = [
    { label: 'Attendance records', count: block?.attendance || 0, icon: <AssignmentOutlined sx={{ fontSize: 18 }} />, tone: theme.palette.info.main },
    { label: 'Exam results',       count: block?.results || 0,    icon: <Receipt sx={{ fontSize: 18 }} />,           tone: theme.palette.warning.main },
    { label: 'Letters issued',     count: block?.letters || 0,    icon: <MailIcon sx={{ fontSize: 18 }} />,          tone: theme.palette.primary.main },
    { label: 'Fine payments',      count: block?.finePayments || 0, icon: <PaymentsOutlined sx={{ fontSize: 18 }} />, tone: theme.palette.success.main },
  ].filter(r => r.count > 0);

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          py: 3, px: 3, textAlign: 'center',
          background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)} 0%, ${alpha(theme.palette.warning.main, 0.14)} 100%)`,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close"
          sx={{
            position: 'absolute', top: 8, right: 8,
            color: 'text.disabled',
            '&:hover': { color: 'text.secondary', bgcolor: alpha(theme.palette.text.primary, 0.04) },
          }}
        >
          <Close sx={{ fontSize: 18 }} />
        </IconButton>

        <Box
          sx={{
            width: 56, height: 56, mx: 'auto', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'background.paper',
            border: `2px solid ${alpha(theme.palette.warning.main, 0.4)}`,
            color: theme.palette.warning.main,
          }}
        >
          <WarningAmberOutlined sx={{ fontSize: 30 }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mt: 1.5, lineHeight: 1.2 }}>
          Can't delete — history exists
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, maxWidth: 380, mx: 'auto' }}>
          {studentName || 'This student'} has records that would be destroyed by a permanent delete. Close their enrollment instead — that preserves the audit trail.
        </Typography>
      </Box>

      <Box sx={{ px: 3, py: 2.5 }}>
        <Typography variant="caption" sx={{ color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600, fontSize: '0.65rem', display: 'block', mb: 1.25 }}>
          What's blocking the delete
        </Typography>
        <Stack divider={<Divider flexItem />} spacing={0}>
          {rows.map(r => (
            <Stack key={r.label} direction="row" alignItems="center" spacing={1.5} sx={{ py: 1 }}>
              <Box
                sx={{
                  width: 32, height: 32, borderRadius: 1.5,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: alpha(r.tone, 0.1), color: r.tone,
                }}
              >
                {r.icon}
              </Box>
              <Typography variant="body2" sx={{ flex: 1, color: 'text.primary' }}>{r.label}</Typography>
              <Chip
                label={r.count.toLocaleString()}
                size="small"
                sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600, bgcolor: alpha(r.tone, 0.1), color: r.tone }}
              />
            </Stack>
          ))}
        </Stack>
      </Box>

      <Box
        sx={{
          px: 3, py: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.background.default, 0.4),
        }}
      >
        <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        {onCloseEnrollment ? (
          <Button
            onClick={onCloseEnrollment}
            variant="contained"
            color="warning"
            disableElevation
            startIcon={<ExitToApp sx={{ fontSize: 18 }} />}
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, px: 2 }}
          >
            Close enrollment instead
          </Button>
        ) : (
          <Button onClick={onClose} variant="contained" disableElevation sx={{ textTransform: 'none', borderRadius: 2 }}>
            Got it
          </Button>
        )}
      </Box>
    </>
  );
};

export default DeleteStudentDialog;
